import axiosInstance from './api';

export const EXTRACT_USER_DETAILS_FILE_FIELD = 'file';

export const EXTRACT_USER_DETAILS_FILE_ACCEPT =
  '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const EXTRACT_USER_DETAILS_FORMAT_HINT = 'CSV or Excel (.csv, .xls, .xlsx)';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return (
    error.message ||
    error?.error?.message ||
    error?.data?.message ||
    error?.response?.data?.message ||
    fallback
  );
};

const createExtractedUserId = (index) =>
  `extracted-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const normalizeExtractedUser = (user, index = 0) => ({
  id: createExtractedUserId(index),
  name: String(user?.name ?? '').trim(),
  email: String(user?.email ?? '').trim(),
  phoneNumber: user?.phoneNumber ?? user?.phone ?? null,
});

export const normalizeExtractedUsers = (users) => {
  if (!Array.isArray(users)) return [];
  return users.map((user, index) => normalizeExtractedUser(user, index));
};

const isPhoneNormalizationWarning = (warning) => {
  const message = String(warning ?? '').toLowerCase();
  return (
    message.includes('phone number could not be normalized') ||
    message.includes('could not be normalized to e.164')
  );
};

export const filterExtractUserDetailsWarnings = (warnings) => {
  if (!Array.isArray(warnings)) return [];
  return warnings.filter((warning) => !isPhoneNormalizationWarning(warning));
};

export const buildEventAttendeesPayload = (users) => ({
  attendees: (Array.isArray(users) ? users : []).map((user) => ({
    name: String(user?.name ?? '').trim(),
    email: String(user?.email ?? '').trim(),
    phoneNumber:
      user?.phoneNumber != null && String(user.phoneNumber).trim() !== ''
        ? String(user.phoneNumber).trim()
        : null,
  })),
});

export const validateEventAttendeesList = (users) => {
  const list = Array.isArray(users) ? users : [];

  if (!list.length) {
    return { valid: false, message: 'Add at least one attendee before submitting.' };
  }

  const invalidIndex = list.findIndex(
    (user) => !String(user?.name ?? '').trim() || !String(user?.email ?? '').trim(),
  );

  if (invalidIndex >= 0) {
    return {
      valid: false,
      message: `Row ${invalidIndex + 1} is missing a name or email.`,
    };
  }

  return { valid: true };
};

export const isExtractUserDetailsFileAllowed = (file) => {
  if (!(file instanceof File)) return false;

  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.xls') || name.endsWith('.xlsx')) {
    return true;
  }

  const type = String(file.type ?? '').toLowerCase();
  return (
    type === 'text/csv' ||
    type === 'application/vnd.ms-excel' ||
    type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

/** POST /admin/extract-user-details — multipart file upload */
export const extractUserDetailsFromFile = async (file) => {
  if (!(file instanceof File)) {
    return { success: false, message: 'Please select a file to upload.' };
  }

  if (!isExtractUserDetailsFileAllowed(file)) {
    return {
      success: false,
      message: 'Please upload a CSV or Excel file with name, email, and phone number columns.',
    };
  }

  const formData = new FormData();
  formData.append(EXTRACT_USER_DETAILS_FILE_FIELD, file);

  try {
    const response = await axiosInstance.post('/admin/extract-user-details', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const payload = response.data;
    const data = payload?.data ?? payload;
    const users = normalizeExtractedUsers(data?.users);

    if (!users.length) {
      return {
        success: false,
        message: payload?.message || 'No user details were found in the uploaded file.',
      };
    }

    return {
      success: true,
      users,
      recordCount: data?.recordCount ?? users.length,
      sourceType: data?.sourceType ?? null,
      warnings: filterExtractUserDetailsWarnings(data?.warnings),
      message: payload?.message || 'User details extracted successfully',
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to extract user details'),
      error,
    };
  }
};
